import { useToastStore, type ToastType } from '../store/toastStore';
import { CheckCircle, AlertTriangle, AlertCircle, Info, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getAvatarColor, getInitials } from '../hooks/useLiveChatNotifications';

const iconMap: Record<ToastType, React.ElementType> = {
  success: CheckCircle,
  error: AlertTriangle,
  warning: AlertCircle,
  info: Info,
};

const styleMap: Record<ToastType, string> = {
  success: 'toast-success',
  error: 'toast-error',
  warning: 'toast-warning',
  info: 'toast-info',
};

const iconColorMap: Record<ToastType, string> = {
  success: 'text-success-400',
  error: 'text-danger-400',
  warning: 'text-warning-400',
  info: 'text-primary-300',
};

/**
 * Toast v2 container (PRD §4.8, T-11). Always mounted (at the app root) as a
 * named region. Screen readers hear toasts through the LiveAnnouncer, so the
 * stack itself is not a live region (no double announcement). Hover or focus
 * pauses a toast's clock; errors stay until closed.
 */
export default function ToastContainer() {
  const { toasts, removeToast, pauseToast, resumeToast } = useToastStore();
  const { t: tr } = useTranslation();

  return (
    // Bottom-centre stack, above the QuickCreate button's row (`.toast-dock`): clear
    // of the button, the timer capsule beside it and the mobile tab bar.
    <section
      aria-label={tr('shell.toastRegion')}
      className="toast-dock fixed inset-x-0 z-toast flex flex-col items-center gap-2 px-4 pointer-events-none"
    >
      {toasts.map((t) => {
        const Icon = t.icon ?? iconMap[t.type];
        return (
          <div
            key={t.id}
            className={`${styleMap[t.type]} pointer-events-auto`}
            data-toast-type={t.type}
            onMouseEnter={() => pauseToast(t.id)}
            onMouseLeave={() => resumeToast(t.id)}
            onFocus={() => pauseToast(t.id)}
            onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) resumeToast(t.id); }}
          >
            {t.avatar ? (
              <span
                aria-hidden="true"
                data-toast-avatar=""
                className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white ${getAvatarColor(t.avatar)}`}
              >
                {getInitials(t.avatar)}
              </span>
            ) : (
              <Icon className={`w-5 h-5 flex-shrink-0 ${iconColorMap[t.type]}`} aria-hidden="true" />
            )}
            <p className={`flex-1 text-sm font-medium ${t.icon ? 'whitespace-pre-line' : ''}`}>{t.message}</p>
            {t.action && (
              <button
                type="button"
                className="toast-action"
                onClick={() => { t.action?.onClick(); removeToast(t.id); }}
              >
                {t.action.label}
              </button>
            )}
            <button
              type="button"
              onClick={() => removeToast(t.id)}
              className="p-1 rounded-md hover:bg-surface-0/10 transition-colors flex-shrink-0"
              aria-label={tr('aria.close')}
            >
              <X className="w-4 h-4 opacity-60" aria-hidden="true" />
            </button>
          </div>
        );
      })}
    </section>
  );
}
